import React, { useState, useEffect, useCallback } from 'react';
import choiceTypes from '../../../shared/choice-types.js';

const API = import.meta.env.VITE_API_BASE_URL || '';
const { PendingChoiceType } = choiceTypes;

// Tipi di pendingChoice gestiti nel frontend (Play V2)
const UI_HANDLED = new Set([
  PendingChoiceType.DISCARD,
  PendingChoiceType.DEATH_REPLACEMENT,
  PendingChoiceType.VISION,
  PendingChoiceType.WEAPONMASTER,
  PendingChoiceType.CHOOSE_ONE,
  PendingChoiceType.CHOOSE_REVEALED_CARD,
  PendingChoiceType.CHOOSE_RUNES,
  PendingChoiceType.REPEAT,
  PendingChoiceType.TOKEN_COPY,
  PendingChoiceType.CHOOSE_TARGET,
  PendingChoiceType.CHOOSE_BATTLEFIELD,
  PendingChoiceType.CHOOSE_UNIT_PAIR,
  PendingChoiceType.CHOOSE_UNITS,
  PendingChoiceType.CHOOSE_UNIT_AND_SPELL,
  // CHOOSE_BATTLEFIELD_UNIT: deprecated, unused by engine — use CHOOSE_TARGET
  PendingChoiceType.CHOOSE_CARD_FROM_HAND,
  PendingChoiceType.CHOOSE_CARD_FROM_ZONE,
  PendingChoiceType.CHOOSE_CARDS_FROM_ZONE,
  // gear targeting auto-pauses via choose_target when pool > 1
  'kill_gear', 'exhaust_gear', 'recall_gear', 'ready_gear',
]);

// Effetti che richiedono interazione UI (producono pendingChoice)
const UI_NEEDED = new Set([
  'predict', 'choose_unit', 'choose_player', 'choose_player_then', 'choose_tag',
  'look_top_pick_recycle', 'look_top',
  'reveal_until_play_recycle', 'draw_revealed', 'each_opponent_choose_then',
  'baited_hook', 'optional_additional_cost',
  'pay_then', 'pay_any_then_damage', 'pay_any_add', 'spend_all_buffs',
]);

const EXISTING_HANDLERS_EXAMPLE = `Handler già implementati in Play V2 (pendingChoice?.type):
- 'discard': overlay per scegliere N carte da scartare dalla mano; invia resolve_discard {cardIds}
- 'death_replacement': conferma o rifiuta una sostituzione di morte; invia resolve_death_replacement {accept}
- 'choose_target': mostra candidati con immagini; click invia resolve_choose_target {unitId}
- 'choose_one': overlay con bottoni per scegliere tra opzioni; invia resolve_choose_one {choiceIndex}
- 'choose_runes': multi-pick opzionale fino al massimo indicato; invia resolve_choose_runes {runeIds}
- 'vision': mostra carta rivelata; conferma con bottone; invia resolve_vision
- 'weaponmaster': lista gear selezionabile; invia resolve_weaponmaster {gearId}
- 'repeat': chiede se pagare per ripetere; invia resolve_repeat {repeat: bool}
- 'choose_battlefield': overlay teal con lista battlefield; invia resolve_choose_battlefield {battlefieldId}
- 'choose_unit_pair': overlay viola con multi-pick 2 unità; invia resolve_choose_unit_pair {unitIds:[id1,id2]}
- 'choose_unit_and_spell': overlay blu 2 step (unit poi spell); invia resolve_choose_unit_and_spell {unitId|spellId}`;

function FixUIModal({ effectType, onClose }) {
  const [code, setCode] = React.useState(null);
  const [desc, setDesc] = React.useState('');
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    fetch(`${API}/api/card-debug/effect-code?type=${encodeURIComponent(effectType)}`)
      .then(r => r.json())
      .then(d => setCode(d.code || '// nessun codice trovato'))
      .catch(() => setCode('// errore nel caricamento'));
  }, [effectType]);

  const fullPrompt = `Implementa il pendingChoice handler per l'effetto "${effectType}" in Play V2.

## Codice engine (card-effects.js)
\`\`\`js
${code ?? '// caricamento...'}
\`\`\`

## Handler esistenti come riferimento
${EXISTING_HANDLERS_EXAMPLE}

## File da modificare
frontend/src/playmat/overlays/registry.js
frontend/src/playmat-v2/state/rulesPresentation.js

## Cosa deve fare l'UI
${desc || '(descrivi qui cosa deve vedere/fare il giocatore)'}

## Istruzioni
1. Aggiungi la logica \`pendingChoice?.type === '${effectType}'\` nel registry overlay o in Play V2 seguendo lo stesso pattern degli handler esistenti.
2. Il giocatore deve interagire e poi inviare \`socket.emit('game_action', { action: 'resolve_${effectType}', params: {...} })\`.
3. Gestisci il caso in game-session.js se serve una nuova action.
4. Aggiungi un test in effects-test.js se cambi l'engine.
5. Rebuild: \`docker compose build frontend && docker compose up -d --no-deps frontend\``;

  const copy = () => {
    navigator.clipboard.writeText(fullPrompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col gap-3 p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-bold text-white">Risolvi UI: <span className="text-orange-400">{effectType}</span></div>
            <div className="text-gray-500 text-xs">Genera un prompt Claude pronto per implementare questo handler</div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl">×</button>
        </div>

        {code === null ? (
          <div className="text-gray-500 text-xs animate-pulse">Caricamento codice engine...</div>
        ) : (
          <div className="bg-gray-950 rounded p-3 overflow-auto max-h-40 text-xs font-mono text-green-300">
            {code}
          </div>
        )}

        <div>
          <label className="text-gray-400 text-xs mb-1 block">Descrivi cosa deve fare la UI (opzionale, migliora il prompt):</label>
          <textarea
            className="w-full bg-gray-800 border border-gray-600 rounded p-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-400 resize-none"
            rows={3}
            placeholder={`Es: mostrare le prime N carte del mazzo in un overlay, il giocatore le trascina per riordinarle, poi conferma`}
            value={desc}
            onChange={e => setDesc(e.target.value)}
          />
        </div>

        <div className="bg-gray-950 rounded p-3 overflow-auto max-h-48 text-xs font-mono text-gray-300 whitespace-pre-wrap">
          {fullPrompt}
        </div>

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Chiudi</button>
          <button
            onClick={copy}
            className={`px-4 py-2 text-sm font-bold rounded transition-colors ${copied ? 'bg-green-700 text-green-200' : 'bg-blue-700 hover:bg-blue-600 text-white'}`}
          >
            {copied ? '✓ Copiato!' : 'Copia prompt Claude'}
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS_CONFIG = {
  ok:      { label: 'OK',      bg: 'bg-green-900/60',  border: 'border-green-700', text: 'text-green-300' },
  partial: { label: 'PARZIALE', bg: 'bg-yellow-900/60', border: 'border-yellow-700', text: 'text-yellow-300' },
  empty:   { label: 'VUOTO',   bg: 'bg-red-900/60',    border: 'border-red-700',    text: 'text-red-300' },
};

function AbilityTree({ ability, depth = 0, engineSet, onFixUI }) {
  const indent = depth * 12;
  return (
    <div style={{ marginLeft: indent }} className="text-xs font-mono text-gray-300 leading-relaxed">
      <div>
        <span className="text-blue-400">{ability.trigger}</span>
        {ability.type && ability.type !== ability.trigger && <span className="text-gray-500"> type={ability.type}</span>}
        {ability.condition && <span className="text-yellow-400"> [{typeof ability.condition === 'string' ? ability.condition : JSON.stringify(ability.condition)}]</span>}
        {ability.scope && <span className="text-purple-400"> scope={ability.scope}</span>}
      </div>
      {ability.effect && (
        <div style={{ marginLeft: 12 }}>
          <EffectLine effect={ability.effect} engineSet={engineSet} onFixUI={onFixUI} />
        </div>
      )}
      {(ability.effects || []).map((e, i) => (
        <div key={i} style={{ marginLeft: 12 }}>
          <EffectLine effect={e} engineSet={engineSet} onFixUI={onFixUI} />
        </div>
      ))}
      {ability.keyword && <div style={{ marginLeft: 12 }} className="text-green-400">keyword: {ability.keyword}</div>}
      {ability.cost && <div style={{ marginLeft: 12 }} className="text-orange-400">cost: {JSON.stringify(ability.cost)}</div>}
    </div>
  );
}

function EffectLine({ effect, depth = 0, engineSet, onFixUI }) {
  if (!effect || typeof effect !== 'object') return null;
  const indent = depth * 10;
  const type = effect.type;

  const engineOk = engineSet ? engineSet.has(type) : null;
  const needsUi = UI_NEEDED.has(type);
  const uiOk = UI_HANDLED.has(type);

  return (
    <div style={{ marginLeft: indent }} className="text-xs font-mono">
      <span className="flex items-center gap-1 flex-wrap">
        <span className="text-green-400">{type}</span>
        {effect.target && <span className="text-gray-400">→ {effect.target}</span>}
        {effect.value !== undefined && <span className="text-cyan-400">={effect.value}</span>}
        {effect.count !== undefined && <span className="text-cyan-400">×{effect.count}</span>}
        {effect.name && <span className="text-gray-300">"{effect.name}"</span>}
        {effect.keyword && <span className="text-green-300">[{effect.keyword}]</span>}
        {effect.condition && <span className="text-yellow-400">?[{typeof effect.condition === 'string' ? effect.condition : JSON.stringify(effect.condition)}]</span>}
        {engineOk !== null && (
          <span className={`text-[9px] px-1 rounded font-bold ${engineOk ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'}`}>
            {engineOk ? '✓ ENGINE' : '✗ ENGINE'}
          </span>
        )}
        {needsUi && (
          <>
            <span className={`text-[9px] px-1 rounded font-bold ${uiOk ? 'bg-green-900 text-green-300' : 'bg-orange-900 text-orange-300'}`}>
              {uiOk ? '✓ UI' : '✗ UI'}
            </span>
            {!uiOk && onFixUI && (
              <button
                onClick={e => { e.stopPropagation(); onFixUI(type); }}
                className="text-[9px] px-1 rounded font-bold bg-blue-900 text-blue-300 hover:bg-blue-700 transition-colors"
              >
                Risolvi
              </button>
            )}
          </>
        )}
      </span>
      {effect.effect && (
        <div style={{ marginLeft: 10 }}>
          <span className="text-gray-500">then: </span><EffectLine effect={effect.effect} depth={depth + 1} engineSet={engineSet} onFixUI={onFixUI} />
        </div>
      )}
      {(effect.effects || []).map((e, i) => (
        <div key={i} style={{ marginLeft: 10 }}>
          <EffectLine effect={e} depth={depth + 1} engineSet={engineSet} onFixUI={onFixUI} />
        </div>
      ))}
      {effect.then && (
        <div style={{ marginLeft: 10 }}>
          <span className="text-gray-500">then: </span><EffectLine effect={effect.then} depth={depth + 1} engineSet={engineSet} onFixUI={onFixUI} />
        </div>
      )}
    </div>
  );
}

function CardInspector({ card, engineSet, onFixUI }) {
  const cfg = STATUS_CONFIG[card.status] || STATUS_CONFIG.empty;
  return (
    <div className={`rounded border ${cfg.border} ${cfg.bg} p-4 h-full flex flex-col gap-3`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-bold text-white text-sm">{card.name}</div>
          <div className="text-gray-500 text-xs">{card.id} · {card.type || '?'}</div>
        </div>
        <span className={`text-xs font-bold px-2 py-0.5 rounded border ${cfg.border} ${cfg.text} shrink-0`}>{cfg.label}</span>
      </div>

      {card.unwiredTriggers?.length > 0 && (
        <div className="bg-yellow-900/40 rounded px-2 py-1 text-yellow-300 text-xs">
          ⚠ Trigger non collegati: {card.unwiredTriggers.join(', ')}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 flex-1 min-h-0">
        {/* Testo originale */}
        <div className="flex flex-col gap-1 min-h-0">
          <div className="text-gray-500 uppercase tracking-wide" style={{ fontSize: 9 }}>Testo carta</div>
          <div className="bg-gray-900/60 rounded p-2 text-xs text-gray-200 leading-relaxed overflow-auto flex-1" style={{ minHeight: 120 }}>
            {card.text
              ? card.text
                .replace(/:rb_\w+:/g, '◆')
                .split('\n').map((line, i) => <div key={i}>{line || <br />}</div>)
              : <span className="text-gray-600">— nessun testo —</span>
            }
          </div>
        </div>

        {/* Abilità compilate */}
        <div className="flex flex-col gap-1 min-h-0">
          <div className="text-gray-500 uppercase tracking-wide" style={{ fontSize: 9 }}>Abilità compilate</div>
          <div className="bg-gray-900/60 rounded p-2 overflow-auto flex-1" style={{ minHeight: 120 }}>
            {card.abilities?.length > 0
              ? card.abilities.map((ab, i) => <AbilityTree key={i} ability={ab} engineSet={engineSet} onFixUI={onFixUI} />)
              : <span className="text-red-400 text-xs">Nessuna abilità compilata</span>
            }
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CardDebug() {
  const [search, setSearch] = useState('');
  const [cards, setCards] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');
  const [engineSet, setEngineSet] = useState(null);
  const [fixUIType, setFixUIType] = useState(null);

  const fetchCards = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/card-debug?search=${encodeURIComponent(q)}`);
      const data = await res.json();
      setCards(data.cards || []);
      setTotal(data.total || 0);
      setSelected(null);
      if (data.implementedEffects) setEngineSet(new Set(data.implementedEffects));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchCards(search), 300);
    return () => clearTimeout(t);
  }, [search, fetchCards]);

  const filtered = filter === 'all' ? cards : cards.filter(c => c.status === filter);
  const counts = { ok: 0, partial: 0, empty: 0 };
  for (const c of cards) counts[c.status] = (counts[c.status] || 0) + 1;

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold">Card Debug</h1>
          <div className="text-gray-500 text-xs">
            {loading ? 'Caricamento…' : `${total} carte totali · mostrate ${filtered.length}`}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => setFilter(filter === key ? 'all' : key)}
              className={`px-3 py-1 rounded border text-xs font-bold transition-colors ${filter === key ? `${cfg.bg} ${cfg.border} ${cfg.text}` : 'border-gray-700 text-gray-500 hover:text-gray-300'}`}
            >
              {cfg.label} {counts[key] || 0}
            </button>
          ))}
        </div>
      </div>

      <input
        type="text"
        placeholder="Cerca per nome o ID carta…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500"
      />

      <div className="flex gap-4 flex-1 min-h-0" style={{ height: 'calc(100vh - 200px)' }}>
        {/* Lista carte */}
        <div className="w-72 shrink-0 overflow-y-auto flex flex-col gap-1 pr-1">
          {filtered.map(card => {
            const cfg = STATUS_CONFIG[card.status] || STATUS_CONFIG.empty;
            return (
              <button
                key={card.id}
                onClick={() => setSelected(card)}
                className={`w-full text-left rounded border px-3 py-2 transition-colors text-xs ${selected?.id === card.id ? `${cfg.bg} ${cfg.border}` : 'border-gray-800 bg-gray-900/40 hover:bg-gray-800/60'}`}
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="font-medium text-white truncate">{card.name}</span>
                  <span className={`shrink-0 font-bold ${cfg.text}`}>{cfg.label[0]}</span>
                </div>
                <div className="text-gray-600 truncate" style={{ fontSize: 9 }}>{card.id}</div>
              </button>
            );
          })}
          {filtered.length === 0 && !loading && (
            <div className="text-gray-600 text-sm text-center py-8">Nessun risultato</div>
          )}
        </div>

        {/* Inspector */}
        <div className="flex-1 min-h-0 overflow-auto">
          {selected
            ? <CardInspector card={selected} engineSet={engineSet} onFixUI={setFixUIType} />
            : (
              <div className="h-full flex items-center justify-center text-gray-700 text-sm">
                ← Seleziona una carta per ispezionarla
              </div>
            )
          }
        </div>
      </div>

      {fixUIType && (
        <FixUIModal effectType={fixUIType} onClose={() => setFixUIType(null)} />
      )}
    </div>
  );
}
